import React, { FormEvent, useRef, useState } from "react";
import "./styles.css";

const benefits = [
  {
    title: "Chiarezza immediata",
    text: "Documenti più facili da leggere, consultare e utilizzare.",
  },
  {
    title: "Coerenza totale",
    text: "Uno standard uniforme tra prodotti, team, versioni e canali.",
  },
  {
    title: "Valore percepito più alto",
    text: "Una documentazione curata rafforza prodotto, servizio e brand.",
  },
];

const services = [
  {
    title: "Audit documentale",
    text: "Analizzo la documentazione esistente per individuare punti deboli, incoerenze, duplicazioni e margini di miglioramento.",
  },
  {
    title: "Manuali e procedure",
    text: "Riorganizzo manuali, guide e istruzioni operative per renderli più leggibili, coerenti e facili da consultare.",
  },
  {
    title: "Template e standard",
    text: "Definisco modelli, regole e strutture riutilizzabili per uniformare la documentazione tra prodotti, team e versioni.",
  },
  {
    title: "Revisione contenuti",
    text: "Aggiorno, semplifico e riscrivo contenuti tecnici per renderli più chiari, precisi e facili da mantenere.",
  },
  {
    title: "Impaginazione tecnica",
    text: "Impagino manuali, schede tecniche e documenti aziendali con layout ordinati, professionali e coerenti con il brand.",
  },
  {
    title: "Schemi e visual tecnici",
    text: "Creo schemi, tabelle, icone e supporti visivi per spiegare meglio procedure, istruzioni e informazioni tecniche.",
  },
];

const targetAudiences = [
  {
    title: "Aziende manifatturiere",
    text: "Per chi produce macchinari, componenti, impianti, arredi tecnici o prodotti che richiedono istruzioni chiare.",
  },
  {
    title: "Studi tecnici",
    text: "Per chi deve presentare procedure, relazioni, guide, schede o documenti tecnici in modo ordinato e professionale.",
  },
  {
    title: "Team prodotto e assistenza",
    text: "Per chi vuole rendere più chiara la documentazione interna, commerciale o destinata al cliente finale.",
  },
];

const startingPoints = [
  "Manuali già esistenti",
  "File Word o PDF",
  "Tabelle tecniche",
  "Screenshot e immagini prodotto",
  "Procedure interne",
  "Appunti tecnici",
  "Documenti da uniformare",
];

const contentTypes = [
  "Manuali d’uso e utente",
  "Guide di installazione e configurazione",
  "Schede tecniche e datasheet",
  "Procedure operative e interne",
  "Note di rilascio e changelog",
  "Documentazione API e tecnica avanzata",
];

const methodPoints = [
  "Struttura dei contenuti",
  "Chiarezza terminologica",
  "Coerenza visiva",
  "Riutilizzo dei contenuti",
  "Mantenibilità nel tempo",
];

const processSteps = [
  {
    number: "01",
    title: "Analisi",
    text: "Analizzo la documentazione esistente per capire criticità, priorità e obiettivi.",
  },
  {
    number: "02",
    title: "Riorganizzazione",
    text: "Riorganizzo struttura, tono, gerarchia dei contenuti e presentazione visiva.",
  },
  {
    number: "03",
    title: "Consegna",
    text: "Ti consegno documenti più chiari, ordinati e semplici da aggiornare.",
  },
];

const practicalResults = [
  "Più facile da capire",
  "Più veloce da aggiornare",
  "Più solida da presentare",
];

export default function App() {
  const compareScrollRef = useRef<HTMLDivElement | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [formStatus, setFormStatus] = useState("");

  const closeMobileNav = () => setIsMobileNavOpen(false);

  const scrollCompare = (direction: number) => {
    if (!compareScrollRef.current) return;

    const container = compareScrollRef.current;
    const amount = container.clientWidth * 0.82;

    container.scrollBy({
      left: direction * amount,
      behavior: "smooth",
    });
  };

  const handleLeadSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    const nameOrCompany = String(formData.get("nameOrCompany") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const documentStatus = String(formData.get("documentStatus") || "").trim();
    const need = String(formData.get("need") || "").trim();

    if (!nameOrCompany || !email || !need) {
      setFormStatus("Compila nome/azienda, email e richiesta per continuare.");
      return;
    }

    const win = window as any;

    if (typeof win.gtag === "function") {
      win.gtag("event", "generate_lead", {
        lead_source: "website_form",
        method: "api_email",
      });
    }

    if (Array.isArray(win.dataLayer)) {
      win.dataLayer.push({
        event: "generate_lead",
        lead_source: "website_form",
        method: "api_email",
      });
    }

    setFormStatus("Invio della richiesta in corso...");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nameOrCompany,
          email,
          documentStatus,
          need,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setFormStatus(
          result.message || "Errore durante l’invio della richiesta."
        );
        return;
      }

      setFormStatus(
        "Richiesta inviata correttamente. Ti risponderò il prima possibile."
      );

      form.reset();
    } catch (error) {
      setFormStatus("Errore di connessione. Riprova tra qualche minuto.");
    }
  };

  return (
    <div className="page" id="top">
      <header className="site-header">
        <div className="container header-row">
          <a
            href="#top"
            className="brand"
            aria-label="Luxa Studio"
            onClick={closeMobileNav}
          >
            <img src="/logo.png" alt="Luxa" className="brand-logo" />
          </a>

          <nav className="main-nav" aria-label="Navigazione principale desktop">
            <a href="#vantaggi" onClick={closeMobileNav}>
              Vantaggi
            </a>
            <a href="#servizi" onClick={closeMobileNav}>
              Servizi
            </a>
            <a href="#per-chi" onClick={closeMobileNav}>
              Per chi
            </a>
            <a href="#partenza" onClick={closeMobileNav}>
              Da cosa partiamo
            </a>
            <a href="#processo" onClick={closeMobileNav}>
              Come lavoro
            </a>
            <a href="#confronto" onClick={closeMobileNav}>
              Confronto
            </a>
            <a href="#contatti" onClick={closeMobileNav}>
              Contatti
            </a>
          </nav>

          <button
            className="mobile-nav-toggle"
            type="button"
            aria-label={isMobileNavOpen ? "Chiudi menu" : "Apri menu"}
            aria-expanded={isMobileNavOpen}
            aria-controls="mobile-nav"
            onClick={() => setIsMobileNavOpen((open) => !open)}
          >
            {isMobileNavOpen ? "Chiudi" : "Menu"}
          </button>
        </div>

        <div
          className={`container mobile-nav-wrap ${
            isMobileNavOpen ? "open" : ""
          }`}
        >
          <nav
            id="mobile-nav"
            className="mobile-nav"
            aria-label="Navigazione principale mobile"
          >
            <a href="#vantaggi" onClick={closeMobileNav}>
              Vantaggi
            </a>
            <a href="#servizi" onClick={closeMobileNav}>
              Servizi
            </a>
            <a href="#per-chi" onClick={closeMobileNav}>
              Per chi
            </a>
            <a href="#partenza" onClick={closeMobileNav}>
              Da cosa partiamo
            </a>
            <a href="#processo" onClick={closeMobileNav}>
              Come lavoro
            </a>
            <a href="#confronto" onClick={closeMobileNav}>
              Confronto
            </a>
            <a href="#contatti" onClick={closeMobileNav}>
              Contatti
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">DIAMO LUCE ALLA TUA DOCUMENTAZIONE</p>

              <h1>
                Documentazione
                <br />
                tecnica chiara.
                <span className="hero-title-gap"></span>
                Ordinata.
                <br />
                Utilizzabile.
                <br />
                Professionale.
              </h1>

              <p className="hero-text">
                Ti aiuto a trasformare manuali, procedure e contenuti tecnici in
                documenti più chiari, ordinati e professionali.
              </p>

              <p className="hero-subline">
                Per aziende, studi tecnici e team prodotto che vogliono rendere
                la documentazione più utile, coerente e semplice da mantenere.
              </p>

              <div className="hero-actions">
                <a href="#contatti" className="btn btn-primary">
                  Richiedi una valutazione gratuita
                </a>

                <a href="#esempio" className="btn btn-secondary">
                  Guarda cosa posso migliorare
                </a>
              </div>
            </div>

            <div className="hero-visual">
              <div className="hero-image-wrap">
                <img
                  src="/hero-image.png"
                  alt="Documentazione tecnica professionale Luxa"
                  className="hero-image"
                  width="1200"
                  height="1060"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="statement-section">
          <div className="container">
            <div className="statement-box">
              <p className="eyebrow">Posizionamento</p>
              <h2>
                Una buona documentazione
                <br />
                non spiega solo meglio.
              </h2>
              <p>
                Rende il prodotto più credibile, più chiaro e più solido. Con
                Luxa, la documentazione tecnica diventa uno strumento coerente
                con il livello del tuo brand.
              </p>
            </div>
          </div>
        </section>

        <section id="vantaggi" className="benefits-section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Vantaggi</p>
              <h2>
                Non è solo documentazione.
                <br />È fiducia costruita bene.
              </h2>
            </div>

            <div className="benefits-grid">
              {benefits.map((item) => (
                <article className="benefit-card" key={item.title}>
                  <div className="benefit-topline"></div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="practical-results-section">
          <div className="container">
            <div className="practical-results-box">
              <div className="practical-results-copy">
                <p className="eyebrow">Cosa cambia in pratica</p>
                <h2>
                  Meno attrito interno.
                  <br />
                  Più valore percepito fuori.
                </h2>
              </div>

              <div className="practical-results-list">
                {practicalResults.map((item) => (
                  <div className="practical-result-item" key={item}>
                    <span className="practical-result-dot"></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="servizi" className="services-section">
          <div className="container">
            <div className="section-head services-head">
              <p className="eyebrow">Servizi</p>
              <h2>
                Cosa posso fare concretamente
                <br />
                per la tua documentazione tecnica.
              </h2>
              <p className="services-intro">
                Intervengo sia su documenti già esistenti, sia nella creazione
                di nuovi materiali tecnici partendo da bozze, appunti, file
                interni o contenuti non strutturati.
              </p>
            </div>

            <div className="services-grid">
              {services.map((service) => (
                <article className="service-card" key={service.title}>
                  <div className="service-topline"></div>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="per-chi" className="audience-section">
          <div className="container">
            <div className="section-head audience-head">
              <p className="eyebrow">Per chi è pensato</p>
              <h2>
                Per chi lavora con prodotti,
                <br />
                processi e informazioni tecniche.
              </h2>
            </div>

            <div className="audience-grid">
              {targetAudiences.map((item) => (
                <article className="audience-card" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="partenza" className="starting-section">
          <div className="container">
            <div className="starting-box">
              <div className="starting-copy">
                <p className="eyebrow">Da cosa possiamo partire</p>
                <h2>
                  Anche un documento disordinato
                  <br />
                  può diventare una base solida.
                </h2>
                <p>
                  Posso lavorare su materiali già pronti, bozze, file interni,
                  screenshot, tabelle o contenuti ancora non strutturati.
                  L’obiettivo è trasformare ciò che esiste in una documentazione
                  più chiara, coerente e utilizzabile.
                </p>
              </div>

              <div className="starting-list">
                {startingPoints.map((item) => (
                  <div className="starting-item" key={item}>
                    <span className="starting-dot"></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contenuti" className="content-section">
          <div className="container content-grid">
            <div className="content-copy">
              <p className="eyebrow">Contenuti</p>
              <h2>Ogni contenuto tecnico può trovare la sua forma.</h2>
              <p>
                Posso aiutarti a organizzare materiali diversi, dal documento
                singolo a un sistema documentale più ampio.
              </p>
            </div>

            <div className="content-list">
              {contentTypes.map((item) => (
                <div className="content-item" key={item}>
                  <span>{item}</span>
                  <span className="content-meta">Gestito con metodo</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="method-section">
          <div className="container">
            <div className="method-box">
              <div className="method-copy">
                <p className="eyebrow">Metodo tecnico, approccio pratico</p>
                <h2>
                  Struttura, chiarezza
                  <br />
                  e mantenibilità.
                </h2>
                <p>
                  Lavoro sulla documentazione con attenzione a ordine dei
                  contenuti, coerenza terminologica, leggibilità, riutilizzo
                  delle informazioni e aggiornabilità nel tempo.
                </p>
              </div>

              <div className="method-grid">
                {methodPoints.map((item) => (
                  <div className="method-item" key={item}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="processo" className="process-section">
          <div className="container">
            <div className="section-head process-head">
              <p className="eyebrow">Come lavoro</p>
              <h2>
                Un processo semplice,
                <br />
                pensato per portare ordine.
              </h2>
            </div>

            <div className="process-grid">
              {processSteps.map((step) => (
                <article className="process-card" key={step.number}>
                  <div className="process-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="confronto" className="compare-showcase-section">
          <div className="container">
            <div className="section-head compare-head">
              <p className="eyebrow">Prima / Dopo</p>
              <h2>
                La stessa documentazione.
                <br />
                Due percezioni completamente diverse.
              </h2>
              <p className="compare-intro">
                Ecco cosa cambia quando la documentazione smette di essere un
                allegato disordinato e diventa parte dell’esperienza del
                prodotto.
              </p>
            </div>

            <div className="compare-scroll-wrap">
              <button
                className="compare-nav compare-nav-left"
                type="button"
                aria-label="Scorri a sinistra"
                onClick={() => scrollCompare(-1)}
              >
                ‹
              </button>

              <div
                className="compare-scroll"
                ref={compareScrollRef}
                role="region"
                aria-label="Confronto prima e dopo della documentazione"
              >
                <article className="compare-panel compare-before">
                  <div className="compare-badge">Prima</div>

                  <div className="compare-mockup compare-mockup-before">
                    <div className="mock-line w-82"></div>
                    <div className="mock-line w-58"></div>
                    <div className="mock-line w-90"></div>

                    <div className="mock-space"></div>

                    <div className="mock-line w-47"></div>
                    <div className="mock-line w-74"></div>
                    <div className="mock-line w-39"></div>

                    <div className="mock-space"></div>

                    <div className="mock-grid chaotic">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>

                  <div className="compare-copy">
                    <h3>Documentazione frammentata</h3>
                    <ul className="compare-list">
                      <li>Informazioni sparse e poco ordinate</li>
                      <li>Gerarchia visiva debole</li>
                      <li>Aggiornamenti incoerenti</li>
                      <li>Lettura più faticosa</li>
                    </ul>
                  </div>
                </article>

                <article className="compare-panel compare-after">
                  <div className="compare-badge">Con Luxa</div>

                  <div className="compare-mockup compare-mockup-after">
                    <div className="mock-topbar"></div>

                    <div className="mock-row">
                      <div className="mock-line accent w-66"></div>
                      <div className="mock-chip"></div>
                    </div>

                    <div className="mock-line w-86"></div>
                    <div className="mock-line w-71"></div>

                    <div className="mock-space"></div>

                    <div className="mock-band"></div>

                    <div className="mock-space small"></div>

                    <div className="mock-grid clean">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>

                  <div className="compare-copy">
                    <h3>Documentazione strutturata</h3>
                    <ul className="compare-list">
                      <li>Lettura più immediata</li>
                      <li>Maggiore continuità tra documenti</li>
                      <li>Presentazione più professionale</li>
                      <li>Valore percepito più alto</li>
                    </ul>
                  </div>
                </article>
              </div>

              <button
                className="compare-nav compare-nav-right"
                type="button"
                aria-label="Scorri a destra"
                onClick={() => scrollCompare(1)}
              >
                ›
              </button>
            </div>

            <p className="compare-bottom-note">
              Anche piccoli interventi su struttura, layout e coerenza possono
              cambiare radicalmente la percezione di un documento tecnico.
            </p>
          </div>
        </section>

        <section id="esempio" className="sample-section">
          <div className="container">
            <div className="sample-box">
              <p className="eyebrow">Esempio gratuito</p>
              <h2>Vuoi vedere come migliorerei un tuo documento?</h2>
              <p>
                Inviami una pagina, una bozza o un estratto della tua
                documentazione: ti mostro gratuitamente dove si può migliorare
                in termini di struttura, chiarezza e presentazione.
              </p>

              <div className="cta-actions">
                <a href="#contatti" className="btn btn-secondary">
                  Richiedi un esempio gratuito
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="contatti" className="cta-section">
          <div className="container">
            <div className="cta-box">
              <p className="eyebrow eyebrow-light">INIZIAMO UN PROGETTO</p>

              <h2>
                Trasforma la tua documentazione tecnica
                <br />
                in un punto di forza del tuo brand.
              </h2>

              <p className="cta-subtitle">
                Scrivimi per una prima valutazione gratuita. Analizzerò la tua
                situazione e ti dirò dove posso intervenire per migliorare
                struttura, leggibilità e consistenza.
              </p>

              <form className="lead-form" onSubmit={handleLeadSubmit} noValidate>
                <div className="lead-grid">
                  <label className="lead-field">
                    <span>Nome / Nome azienda</span>
                    <input
                      type="text"
                      name="nameOrCompany"
                      autoComplete="name"
                      required
                      placeholder="Mario Rossi / Azienda Srl"
                    />
                  </label>

                  <label className="lead-field">
                    <span>Email</span>
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                      placeholder="nome@azienda.it"
                    />
                  </label>

                  <label className="lead-field lead-field-full">
                    <span>Hai già un documento da valutare?</span>
                    <select name="documentStatus" defaultValue="">
                      <option value="" disabled>
                        Seleziona una risposta
                      </option>
                      <option value="Sì, ho già un documento da valutare">
                        Sì, ho già un documento
                      </option>
                      <option value="No, parto da bozze o appunti">
                        No, parto da bozze o appunti
                      </option>
                      <option value="Non so ancora da cosa partire">
                        Non so ancora da cosa partire
                      </option>
                    </select>
                  </label>

                  <label className="lead-field lead-field-full">
                    <span>Di cosa hai bisogno?</span>
                    <textarea
                      name="need"
                      required
                      rows={4}
                      placeholder="Descrivi in breve il documento da migliorare, il formato attuale e l’obiettivo."
                    />
                  </label>
                </div>

                <div className="cta-actions">
                  <button className="btn-cta" type="submit">
                    Richiedi una valutazione gratuita
                  </button>
                </div>
              </form>

              <p className="lead-privacy-note">
                Inviando la richiesta accetti il trattamento dati descritto nella{" "}
                <a href="/privacy">Privacy Policy</a>.
              </p>

              {formStatus ? (
                <p className="lead-status" aria-live="polite">
                  {formStatus}
                </p>
              ) : null}

              <div className="cta-trust">
                ✓ Nessun impegno &nbsp;&nbsp;•&nbsp;&nbsp; Risposta entro 24h
                &nbsp;&nbsp;•&nbsp;&nbsp; Primo confronto gratuito
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/logo_b.png" alt="Luxa" className="brand-logo" />
              <p>
                Luxa Studio — documentazione tecnica, impaginazione e contenuti
                visuali per aziende e team tecnici.
              </p>
              <a href="mailto:info@luxastudio.it" className="footer-mail">
                info@luxastudio.it
              </a>
            </div>

            <div className="footer-links">
              <a href="#vantaggi">Vantaggi</a>
              <a href="#servizi">Servizi</a>
              <a href="#per-chi">Per chi</a>
              <a href="#partenza">Da cosa partiamo</a>
              <a href="#esempio">Esempio</a>
              <a href="#contatti">Contatti</a>
              <a href="/privacy">Privacy</a>
              <a href="/cookie">Cookie</a>
            </div>

            <div className="footer-contact">
              <p>© 2026 Luxa. Tutti i diritti riservati.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
